import moment from "moment"
import Parse from "parse"
import { STOCK_TYPES } from "./stockTypes"

/**
 * KFC-352: the numeric part of a unique code, as the counter staff reads it.
 *
 * Codes mix widths within a single letter — A4, A44 and A157 all exist. Comparing them as
 * strings therefore puts A157 before A4, which is what the screen did until now.
 *
 * A code with no digit at all sorts last rather than poisoning the comparison with NaN:
 * `uniqueCode` falls back to "NC" when it is missing, and a couple of rows in the database
 * carry values like "undefined1".
 */
function uniqueCodeNumber(uniqueCode) {
	const digits = String(uniqueCode ?? "").replace(/\D/g, "")

	return digits === "" ? Number.MAX_SAFE_INTEGER : parseInt(digits, 10)
}

/** The letter part, which also groups the tiles into rows. */
function uniqueCodeLetters(uniqueCode) {
	return String(uniqueCode ?? "").replace(/\d/g, "")
}

/**
 * KFC-352: orders tiles within a letter — by product number, then by DLC.
 *
 * The DLC matters: the same code legitimately appears more than once on a hub, with
 * different expiry dates. The final comparison on the whole code keeps the order stable, so
 * that two rows that are equal on every other count never swap between renders.
 */
export function compareByUniqueCode(a, b) {
	const lettersA = uniqueCodeLetters(a.uniqueCode)
	const lettersB = uniqueCodeLetters(b.uniqueCode)

	if (lettersA !== lettersB) {
		// A code with no letter at all is degenerate, like one with no digit: it goes last
		// rather than ahead of every real product.
		if (lettersA === "") return 1
		if (lettersB === "") return -1

		return lettersA.localeCompare(lettersB)
	}

	const numberA = uniqueCodeNumber(a.uniqueCode)
	const numberB = uniqueCodeNumber(b.uniqueCode)

	if (numberA !== numberB) return numberA - numberB
	if (a.dlc !== b.dlc) return (a.dlc || 0) - (b.dlc || 0)

	return String(a.uniqueCode ?? "").localeCompare(String(b.uniqueCode ?? ""))
}

export function formatReceptionData(receptionData) {
	const newReceptionData = {}

	receptionData.forEach(elm => {
		const group = elm.uniqueCode.replace(/\d/g,"")

		if (!newReceptionData[elm.productType]) newReceptionData[elm.productType] = {}
		if (!newReceptionData[elm.productType][group])  newReceptionData[elm.productType][group] = []
		newReceptionData[elm.productType][group].push(elm)
	})

	return newReceptionData
}

export function handleReceptionEvent(productStock) {
	let tooltipText = ""

	const reception = productStock.events.filter(elm => elm.type === "RECEPTION")

	if (reception.length > 0) {
		// A hub with two fridges records one reception event per fridge; the tooltip announces
		// what was received in total, not what landed in the first of them.
		const received = reception
			.filter(elm => elm.date === reception[0].date)
			.reduce((total, elm) => total + (elm.data.volume || 0), 0)

		tooltipText = moment(reception[0].date).format("DD/MM HH:mm")
		tooltipText = `${received} - ${tooltipText} ${reception[0].user?.username || "unknown"}`
	}

	if (undefined === productStock.commercialTitle) productStock.commercialTitle = ""
	if (undefined === productStock.uniqueCode) productStock.uniqueCode = "NC"

	productStock.display = (productStock.initialVolume > 0)
	productStock.toolTip = tooltipText
	
	return productStock
}

export function findProductStock(receptionData, uniqueCode, dlc= false) {
	let result = receptionData.filter(elm => elm.uniqueCode === uniqueCode)
	if (dlc.length === 10) {
		result = result.filter(elm => moment.utc(elm.dlc).format("DD/MM/YYYY") === dlc)
	} else if (dlc.length === 5) {
		result = result.filter(elm => moment.utc(elm.dlc).format("DD/MM") === dlc)
	}

	return result
}

export function getProductStock(receptionData, uniqueCode, dlc) {
	let result = receptionData.find(el => el.uniqueCode === uniqueCode && el.dlc === dlc)

	return result
}

/**
 * A breakage is booked against one fridge. Reception has no choice to offer — a shortfall on a
 * delivery can only come from click & collect, since the self-service fridge is fed by a transfer
 * — so that stays the default. The counter manager picks the fridge when declaring a breakage.
 */
export function getLostData(lost, date, stockType = STOCK_TYPES.CLICK_AND_COLLECT) {
	return {
		type: "FLAW",
		user: Parse.User.current(),
		date: date,
		data: {
			volume: parseInt(lost.volume),
			reason: lost.reason,
			commentary: lost.explanation,
			stockType
		}
	}
}
